/**
 * ProfileContext - Gestione profilo anagrafica e stats formazione
 * Separato da AuthContext per singola responsabilità.
 * ProfileProvider deve essere figlio di AuthProvider (usa useAuth internamente).
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getAnagraficaProfile } from '../services/profileService';
import { getStats } from '../services/formazioneService';
import { STORAGE_KEYS } from '../utils/constants';
import logger from '../utils/logger';

const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const { user } = useAuth();

  const [profile, setProfile] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEYS.ANAGRAFICA_DATA)); }
    catch { return null; }
  });
  const [stats, setStats] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Carica profilo e stats al cambio utente (login, logout, refresh)
  useEffect(() => {
    if (!user?.idAnagrafico) {
      setProfile(null);
      setStats(null);
      return;
    }

    (async () => {
      setProfileLoading(true);
      try {
        const res = await getAnagraficaProfile(user.idAnagrafico);
        if (res.success) setProfile(res.data);
        else logger.warn('[ProfileContext] Errore caricamento profilo:', res.message);
      } catch (e) {
        logger.error('[ProfileContext] Errore caricamento profilo:', e);
      }

      const isPrivileged = user.roles?.some(r => ['admin', 'segreteria'].includes(r.toLowerCase()));
      if (isPrivileged) {
        try {
          const statsRes = await getStats(user.idAnagrafico, new Date().getFullYear());
          if (statsRes.success) setStats(statsRes.data);
        } catch (e) {
          logger.warn('[ProfileContext] Stats non disponibili:', e);
        }
      }

      setProfileLoading(false);
    })();
  }, [user?.idAnagrafico]);

  // Sync localStorage quando il profilo cambia
  useEffect(() => {
    if (profile) localStorage.setItem(STORAGE_KEYS.ANAGRAFICA_DATA, JSON.stringify(profile));
    else localStorage.removeItem(STORAGE_KEYS.ANAGRAFICA_DATA);
  }, [profile]);

  const refreshProfile = useCallback(async () => {
    if (!user?.idAnagrafico) return;
    const res = await getAnagraficaProfile(user.idAnagrafico);
    if (res.success) setProfile(res.data);
  }, [user?.idAnagrafico]);

  const updateProfile = useCallback((data) => {
    setProfile(prev => ({ ...prev, ...data }));
  }, []);

  const refreshStats = useCallback(async () => {
    if (!user?.idAnagrafico) return;
    const statsRes = await getStats(user.idAnagrafico, new Date().getFullYear());
    if (statsRes.success) setStats(statsRes.data);
  }, [user?.idAnagrafico]);

  return (
    <ProfileContext.Provider value={{ profile, stats, profileLoading, refreshProfile, updateProfile, refreshStats }}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile deve essere usato dentro ProfileProvider');
  return ctx;
}
